import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, ArrowRight, ChevronRight } from "lucide-react";
import { Analytics } from "@/lib/analyticsShim";

const SLIDES = [
  {
    title: "Today's Live Steel Prices",
    subtitle: "Real-time rates from verified mills, updated hourly.",
    image: "https://static.prod-images.emergentagent.com/jobs/94071925-a079-4939-8c65-46424c5d806c/images/877e7b4ab414458c8ca9ade8e71b7355decedd020696d6cafa915fa66b6d0317.png",
    showPrices: true,
    cta: null,
  },
  {
    title: "Cement Supply at Scale",
    subtitle: "UltraTech · Ambuja · ACC — Bulk delivery across India, best mill rates.",
    image: "https://static.prod-images.emergentagent.com/jobs/94071925-a079-4939-8c65-46424c5d806c/images/7883b78340dd6717c6179ecfa1d6bda9873601d0a9a6cc24a8653240d7c8ab28.png",
    cta: { label: "View Cement Prices", href: "/products/cement" },
  },
  {
    title: "Structural Steel — Built to Spec",
    subtitle: "ISMB beams, channels & angles from Tata, JSW, SAIL.",
    image: "https://static.prod-images.emergentagent.com/jobs/94071925-a079-4939-8c65-46424c5d806c/images/bfa639b1c8d2fd883ed8a51c6c0e7cad5819d85b09173062a0c4c4e0f2f555b0.png",
    cta: { label: "Explore Structural Steel", href: "/products/structural-steel" },
  },
  {
    title: "Everything Your Project Needs",
    subtitle: "From earth movers to sanitaryware — one B2B marketplace for infrastructure.",
    image: "https://static.prod-images.emergentagent.com/jobs/94071925-a079-4939-8c65-46424c5d806c/images/5aa4898c452ad25851772b4a3a264a3968b0ea095070b4fef5f28b86a648b689.png",
    cta: { label: "Explore Full Catalogue", action: "categories" },
  },
];

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 4500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/steel-prices").then(({ data }) => setPrices(data.prices || [])).catch(() => {});
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative min-h-[500px] sm:min-h-[460px] md:min-h-[476px] overflow-hidden" data-testid="hero-carousel">
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`fade-slide ${i === idx ? "active" : ""}`}
          style={{ backgroundImage: `url(${s.image})`, backgroundSize: "cover", backgroundPosition: "center" }}
          data-testid={`hero-slide-${i}`}
        />
      ))}
      <div className="absolute inset-0 hero-overlay" />

      <button
        onClick={() => setIdx((i) => (i + 1) % SLIDES.length)}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/35 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
        data-testid="hero-next-slide"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="relative z-10 min-h-[500px] sm:min-h-[460px] md:min-h-[476px] max-w-7xl mx-auto px-6 md:px-10 py-10 pb-16 flex items-center">
        <div className={`w-full grid gap-6 ${idx === 0 && prices.length > 0 ? "lg:grid-cols-[minmax(0,1fr)_minmax(280px,390px)] lg:items-center" : ""}`}>
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C8A96A]/90 text-white px-3 py-1 rounded-sm text-[10px] sm:text-xs uppercase tracking-widest font-semibold mb-4 md:mb-5">
              <TrendingUp className="w-3.5 h-3.5" />
              B2B Procurement · Railway & Infra
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
              {SLIDES[idx].title}
            </h1>
            <p className="mt-3 md:mt-5 text-sm sm:text-lg text-white/85 max-w-xl leading-relaxed">
              {SLIDES[idx].subtitle}
            </p>

            <div className="mt-5 md:mt-8 flex flex-wrap gap-3 md:gap-4">
              {SLIDES[idx].cta?.action === "categories" && (
                <button
                  onClick={() => scrollTo("categories")}
                  className="btn-gold px-4 md:px-6 py-2.5 md:py-3 rounded-md text-sm md:text-base font-semibold inline-flex items-center gap-2"
                  data-testid="hero-cta-categories"
                >
                  {SLIDES[idx].cta.label} <ArrowRight className="w-4 h-4" />
                </button>
              )}
              {SLIDES[idx].cta?.href && (
                <a
                  href={SLIDES[idx].cta.href}
                  className="btn-gold px-4 md:px-6 py-2.5 md:py-3 rounded-md text-sm md:text-base font-semibold inline-flex items-center gap-2"
                  data-testid="hero-cta-product"
                >
                  {SLIDES[idx].cta.label} <ArrowRight className="w-4 h-4" />
                </a>
              )}
              <button
                onClick={() => scrollTo("requirements")}
                className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-md text-sm md:text-base font-semibold hover:bg-white/20 transition-colors"
                data-testid="hero-cta-quote"
              >
                Request a Quote
              </button>
            </div>
          </div>

          {/* Live price tiles - only on slide 0 */}
          {idx === 0 && prices.length > 0 && (
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 md:gap-3 lg:justify-self-end lg:w-full" data-testid="live-price-tiles">
              {prices.map((p, i) => (
                <div
                  key={i}
                  className="bg-white/90 backdrop-blur-md border border-[#C8A96A]/30 p-2 md:p-3 lg:p-4 rounded-md shadow-xl min-w-0"
                  data-testid={`price-tile-${i}`}
                >
                  <div className="text-[9px] sm:text-[10px] md:text-xs uppercase tracking-wider md:tracking-widest text-[#4B5563] font-semibold truncate">{p.brand}</div>
                  <div className="font-display font-bold text-sm sm:text-base md:text-lg lg:text-xl text-[#111827] mt-0.5 md:mt-1">{p.size}</div>
                  <div className="text-[#C8A96A] font-bold text-xs sm:text-sm md:text-base lg:text-lg mt-0.5 md:mt-1 whitespace-nowrap">
                    ₹{p.price.toLocaleString("en-IN")}<span className="text-[9px] md:text-xs text-[#4B5563]">/{p.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dot indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[#C8A96A]" : "w-2 bg-white/50"}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}
